import { useState, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Send, Loader2, MessageSquare, Square, CornerDownRight } from 'lucide-react';
import type { ClarifyingQA } from '../../types';

interface ClarificationControlsProps {
  clarifyingQA: ClarifyingQA[];
  onSubmitAnswers: (answers: Record<number, string>, comment?: string) => void;
  isProcessing: boolean;
  onStop?: () => void;
}

export function ClarificationControls({
  clarifyingQA,
  onSubmitAnswers,
  isProcessing,
  onStop,
}: ClarificationControlsProps) {
  const [answers, setAnswers] = useState<Record<number, string>>({});
  const [comment, setComment] = useState('');

  // Questions that still need answers (answer is null)
  const unansweredQuestions = clarifyingQA
    .map((qa, index) => ({ ...qa, index }))
    .filter((qa) => qa.answer === null);

  // Questions that already have answers
  const answeredQuestions = clarifyingQA.filter((qa) => qa.answer !== null);

  // "Stopped" state: all questions answered but still showing (plan generation was cancelled)
  const allAnswered = unansweredQuestions.length === 0 && answeredQuestions.length > 0;

  const handleAnswerChange = useCallback((index: number, value: string) => {
    setAnswers((prev) => ({ ...prev, [index]: value }));
  }, []);

  const handleSubmit = useCallback(() => {
    // Only submit answers that have content
    const filledAnswers: Record<number, string> = {};
    for (const [key, value] of Object.entries(answers)) {
      const trimmed = value.trim();
      if (trimmed) {
        filledAnswers[Number(key)] = trimmed;
      }
    }
    if (Object.keys(filledAnswers).length === 0) return;
    onSubmitAnswers(filledAnswers);
  }, [answers, onSubmitAnswers]);

  const handleContinue = useCallback(() => {
    // Rebuild answers from the answered Q&A (server already has them)
    const rebuiltAnswers: Record<number, string> = {};
    clarifyingQA.forEach((qa, idx) => {
      if (qa.answer) {
        rebuiltAnswers[idx] = qa.answer;
      }
    });
    const trimmedComment = comment.trim() || undefined;
    onSubmitAnswers(rebuiltAnswers, trimmedComment);
    setComment('');
  }, [clarifyingQA, comment, onSubmitAnswers]);

  const hasFilledAnswers = Object.values(answers).some((v) => v.trim().length > 0);

  if (clarifyingQA.length === 0) return null;

  return (
    <div className="flex-1 flex flex-col p-6 overflow-y-auto">
      <div className="max-w-lg mx-auto w-full space-y-6 flex-1 flex flex-col">
        {/* Header */}
        <div className="text-center space-y-2">
          <MessageSquare className="h-8 w-8 mx-auto text-primary" />
          <h2 className="text-xl font-semibold">
            {allAnswered && !isProcessing ? 'Ready to continue' : 'A few questions first'}
          </h2>
          <p className="text-sm text-muted-foreground">
            {allAnswered && !isProcessing
              ? 'Your answers are saved. Add any additional details or continue to generate the build plan.'
              : 'Answer each question below to help shape your build plan.'}
          </p>
        </div>

        {/* Previously answered questions (read-only) */}
        {answeredQuestions.length > 0 && (
          <div className="space-y-2">
            {answeredQuestions.map((qa, i) => (
              <div key={`answered-${i}`} className="rounded-md bg-muted/30 p-3 space-y-1">
                <p className="text-sm font-medium">{qa.question}</p>
                <p className="text-sm text-muted-foreground">{qa.answer}</p>
              </div>
            ))}
          </div>
        )}

        {/* Unanswered questions with input fields */}
        {unansweredQuestions.length > 0 && (
          <div className="space-y-4 flex-1">
            {unansweredQuestions.map((qa) => (
              <div key={`q-${qa.index}`} className="space-y-1.5">
                <label className="text-sm font-medium" htmlFor={`qa-${qa.index}`}>
                  {qa.question}
                </label>
                <Textarea
                  id={`qa-${qa.index}`}
                  value={answers[qa.index] || ''}
                  onChange={(e) => handleAnswerChange(qa.index, e.target.value)}
                  placeholder="Type your answer..."
                  className="min-h-[60px] resize-none"
                  disabled={isProcessing}
                />
              </div>
            ))}
          </div>
        )}

        {/* Stopped state: comment area for additional clarification */}
        {allAnswered && !isProcessing && (
          <div className="space-y-2">
            <label className="text-sm font-medium" htmlFor="clarification-comment">
              Additional clarification (optional)
            </label>
            <Textarea
              id="clarification-comment"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Anything else you'd like the AI to consider when building the plan..."
              className="min-h-[80px] resize-none"
            />
          </div>
        )}

        {/* Submit / Stop / Continue buttons */}
        <div className="flex justify-center pt-2 gap-2">
          {isProcessing ? (
            <>
              <Button disabled className="flex-1" size="lg" variant="secondary">
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Generating build plan...
              </Button>
              {onStop && (
                <Button onClick={onStop} variant="destructive" size="lg">
                  <Square className="h-3.5 w-3.5 mr-2 fill-current" />
                  Stop
                </Button>
              )}
            </>
          ) : allAnswered ? (
            <Button onClick={handleContinue} className="w-full" size="lg">
              <CornerDownRight className="mr-2 h-4 w-4" />
              {comment.trim() ? 'Continue with Clarification' : 'Continue'}
            </Button>
          ) : (
            <Button
              onClick={handleSubmit}
              disabled={!hasFilledAnswers}
              className="w-full"
              size="lg"
            >
              <Send className="mr-2 h-4 w-4" />
              Submit Answers
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
